import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import LoadingSpinner from './components/LoadingSpinner';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import EmailConfirmationPage from './pages/EmailConfirmationPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ClientDashboard from './pages/ClientDashboard';
import ProviderDashboard from './pages/ProviderDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CheckinPage from './pages/CheckinPage';
import ProtectedRoute from './components/ProtectedRoute';
import { checkBackendHealth, logVersionInfo } from './services/healthService';
import { debugEnvironmentVariables } from './utils/version';

function App() {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Health check on app startup
  useEffect(() => {
    // Debug environment variables first
    debugEnvironmentVariables();
    
    const performHealthCheck = async () => {
      const healthResult = await checkBackendHealth();
      logVersionInfo(healthResult);
    };

    // Run health check after a short delay to ensure app is fully loaded
    const timer = setTimeout(performHealthCheck, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // If not authenticated, show login/register pages
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/confirm-email" element={<EmailConfirmationPage />} />
        <Route path="/checkin/:clientUuid/:timestamp" element={<CheckinPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // If authenticated, route based on user role
  return (
    <Routes>
      {/* Public routes (should redirect if authenticated) */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/register" element={<Navigate to="/" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/" replace />} />
      
      {/* Check-in route (accessible to both authenticated and unauthenticated users) */}
      <Route path="/checkin/:clientUuid/:timestamp" element={<CheckinPage />} />
      
      {/* Role-based routes */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            {user?.userType === 'client' && <ClientDashboard />}
            {user?.userType === 'provider' && <ProviderDashboard />}
            {user?.userType === 'admin' && <AdminDashboard />}
            {!user?.userType && <Navigate to="/login" replace />}
          </ProtectedRoute>
        } 
      />
      
      {/* Client-specific routes */}
      <Route 
        path="/client/*" 
        element={
          <ProtectedRoute requiredRole="client">
            <ClientDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Provider-specific routes */}
      <Route 
        path="/provider/*" 
        element={
          <ProtectedRoute requiredRole="provider">
            <ProviderDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Admin-specific routes */}
      <Route 
        path="/admin/*" 
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Catch all - redirect to appropriate dashboard */}
      <Route 
        path="*" 
        element={
          <ProtectedRoute>
            {user?.userType === 'client' && <Navigate to="/client" replace />}
            {user?.userType === 'provider' && <ProviderDashboard />}
            {user?.userType === 'admin' && <Navigate to="/admin" replace />}
            {!user?.userType && <Navigate to="/login" replace />}
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
}

export default App; 